// components/portal/BrandLogoCarousel.tsx
//
// The chat's live line while a sourcing run has not landed its first brand: the logos of the brands
// picked for analysis roll past, the ones being analysed right now lit, the rest dimmed (owner,
// 2026-09-26). It replaces "Analysing X, Y, Z", which named three brands of a run of forty.
import React from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import type { ChatStatusBrand } from '#types/types';

// One lap holds at least this many marks, so a short list still spans the chat's width.
const MIN_LAP = 16;
const APPEAR_STAGGER_MS = 110;

export const BrandLogoCarousel = ({ brands, label }: { brands: ChatStatusBrand[]; label: string }) => {
  const lap = Array.from({ length: Math.max(1, Math.ceil(MIN_LAP / brands.length)) }, () => brands).flat();
  // Twice over, so the strip scrolls by exactly one lap and loops without a seam.
  const loop = [...lap, ...lap];
  return (
    <div
      role="status"
      aria-label={label}
      title={label}
      className="brand-logo-carousel-in relative min-w-0 flex-1 overflow-hidden"
      style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}
    >
      <div
        className="brand-logo-carousel flex w-max items-center gap-2 py-1"
        style={{ animationDuration: `${Math.max(5, lap.length * 0.7)}s` }}
      >
        {loop.map((brand, index) => (
          <span
            key={`${brand.name}-${index}`}
            aria-hidden={index >= brands.length}
            title={brand.name}
            // The brand being worked on is unmistakable: full opacity, a ring, a touch larger, and
            // the pulse; the rest fade back (owner, 2026-09-27).
            className={brand.active ? 'animate-pulse rounded-md ring-1 ring-white/70 scale-110' : undefined}
            style={{ opacity: brand.active ? 1 : 0.35, transition: 'opacity 300ms, transform 300ms' }}
          >
            {/* One by one, as if the run were adding them (owner, 2026-09-26). */}
            <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={24} radius={6} appear appearDelayMs={(index % lap.length) * APPEAR_STAGGER_MS} />
          </span>
        ))}
      </div>
    </div>
  );
};
